import React, { useMemo } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import { toast } from 'react-toastify';
import { getSafeString } from './campaignAiEngine';

const safeArray = (value) => (Array.isArray(value) ? value : []);
const escapeHtml = (s) =>
  String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const buildPrintHtmlList = (title, items, emptyText) => {
  const rows = safeArray(items).map((item) => getSafeString(item)).filter(Boolean);
  let html = `<h2>${escapeHtml(title)}</h2>`;
  if (!rows.length) {
    html += `<p class="muted">${escapeHtml(emptyText)}</p>`;
    return html;
  }
  html += '<ol>';
  rows.forEach((item) => {
    html += `<li>${escapeHtml(item)}</li>`;
  });
  html += '</ol>';
  return html;
};

const sectionSx = {
  border: '1px solid rgba(122,77,11,0.16)',
  borderRadius: 1.4,
  p: 1.1,
  background: '#ffffff',
  boxShadow: '0 8px 18px rgba(87,65,25,0.06)'
};

const titleSx = {
  color: '#7a4d0b',
  fontSize: '1.08rem',
  fontWeight: 900,
  mb: 0.65
};

const textSx = {
  color: '#2f2516',
  fontSize: '0.98rem',
  lineHeight: 1.48
};

const labelSx = {
  color: '#7a4d0b',
  fontSize: '0.76rem',
  fontWeight: 800,
  letterSpacing: '0.02em',
  textTransform: 'uppercase'
};

const valueSx = {
  color: '#2f2516',
  fontSize: '0.94rem',
  lineHeight: 1.4
};

const buildCopyText = (output) => {
  if (!output) return '';
  return [
    getSafeString(output.title, 'Personel Çıktısı'),
    getSafeString(output.subtitle, 'Personele Paylaşılacak Kampanya Uygulama Dosyası'),
    '',
    'KAMPANYA DETAYLARI',
    `- Kampanya: ${getSafeString(output.campaignDetails?.name, '-')}`,
    `- Tarih: ${getSafeString(output.campaignDetails?.dateRange, '-')}`,
    `- Hedef: ${getSafeString(output.campaignDetails?.goal, '-')}`,
    `- Kampanya Hedefi: ${getSafeString(output.campaignDetails?.targetText, output.campaignDetails?.goal || '-')}`,
    '',
    'MAĞAZA',
    safeArray(output.approvedStorePlan).map((item) => `- ${getSafeString(item, '-')}`).join('\n') || '-',
    '',
    'PERSONEL',
    safeArray(output.approvedStaffPlan).map((item) => `- ${getSafeString(item, '-')}`).join('\n') || '-',
    '',
    'YENİ FİKİR',
    safeArray(output.approvedCustomIdeas).map((item) => `- ${getSafeString(item, '-')}`).join('\n') || '-',
    '',
    `Paylaşım Notu: ${getSafeString(output.staffShareNote, '-')}`
  ].join('\n');
};

const PlanSection = ({ title, items, emptyText }) => (
  <Box className="staff-output-section" sx={sectionSx}>
    <Typography sx={titleSx}>{title}</Typography>
    {safeArray(items).length ? (
      safeArray(items).map((item, idx) => (
        <Box
          className="staff-output-plan-item"
          key={`${title}-${idx}-${getSafeString(item)}`}
          sx={{
            display: 'grid',
            gridTemplateColumns: '34px 1fr',
            gap: 0.85,
            alignItems: 'start',
            mb: 0.75,
            p: 0.85,
            borderRadius: 1.45,
            border: '1px solid rgba(151,113,39,0.16)',
            background: 'rgba(255,255,255,0.72)'
          }}
        >
          <Box
            className="staff-output-plan-number"
            sx={{
              width: 28,
              height: 28,
              borderRadius: '50%',
              display: 'grid',
              placeItems: 'center',
              bgcolor: 'rgba(151,113,39,0.14)',
              color: '#7a4d0b',
              fontWeight: 900,
              fontSize: '0.95rem'
            }}
          >
            {idx + 1}
          </Box>
          <Typography sx={textSx}>{getSafeString(item, '-')}</Typography>
        </Box>
      ))
    ) : (
      <Typography sx={textSx}>{emptyText}</Typography>
    )}
  </Box>
);

function StaffOutputModal({ open, output, hasActiveCampaign = true, onClose }) {
  const copyText = useMemo(() => buildCopyText(output), [output]);
  const printDateRange = useMemo(() => {
    return getSafeString(output?.campaignDetails?.dateRange);
  }, [output]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(copyText);
      toast.success('Personel çıktısı kopyalandı', { autoClose: 1600 });
    } catch (e) {
      toast.error('Kopyalama yapılamadı', { autoClose: 1800 });
    }
  };

  const handlePrint = () => {
    if (!output) return;
    const title = getSafeString(output.title, 'Personel Çıktısı');
    const campaignName = getSafeString(output.campaignDetails?.name, '-');
    const dateRange = getSafeString(output.campaignDetails?.dateRange, '-');
    const goal = getSafeString(output.campaignDetails?.goal, '-');
    const targetText = getSafeString(output.campaignDetails?.targetText, goal || '-');
    const storeSection = buildPrintHtmlList('Mağaza / Vitrin / Banko', output.approvedStorePlan, 'Bu kanalda henüz fikir yok.');
    const staffSection = buildPrintHtmlList('Personel', output.approvedStaffPlan, 'Bu kanalda henüz fikir yok.');
    const customRows = safeArray(output.approvedCustomIdeas).filter(Boolean);
    const customSection = customRows.length ? buildPrintHtmlList('Yeni Fikir', customRows, '') : '';

    const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(title)} - Personel Sunumu</title>
  <style>
    * { box-sizing: border-box; }
    body { font-family: Inter, system-ui, -apple-system, "Segoe UI", sans-serif; margin: 0; color: #1f1a14; background: #f8f4ea; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .page { width: 100%; max-width: 1000px; margin: 0 auto; padding: 22px 18px 20px; }
    .hero { background: linear-gradient(130deg, #fffaf0 0%, #f0e3cd 100%); border: 1px solid rgba(151,113,39,0.35); border-radius: 12px; padding: 16px 18px; margin-bottom: 14px; }
    .hero h1 { font-size: 1.48rem; margin: 0 0 7px; letter-spacing: 0.01em; color: #2f2516; }
    .meta { display: flex; flex-wrap: wrap; gap: 8px; margin: 0; }
    .chip { border: 1px solid rgba(151,113,39,0.3); border-radius: 999px; padding: 4px 10px; font-size: 0.82rem; font-weight: 700; color: #5c4d3a; background: rgba(255,255,255,0.75); }
    .grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; margin-bottom: 10px; }
    .card { border: 1px solid rgba(151,113,39,0.26); border-radius: 10px; padding: 10px 12px; background: #fff; break-inside: avoid; page-break-inside: avoid; }
    .card h2 { margin: 0 0 7px; font-size: 0.98rem; color: #7a4d0b; letter-spacing: 0.01em; }
    .card p { margin: 0.15em 0; line-height: 1.47; font-size: 0.92rem; }
    ol { margin: 0.1em 0 0.2em 1.2em; padding: 0; font-size: 0.92rem; line-height: 1.46; }
    li { margin: 0.26em 0; }
    .full { margin-top: 10px; }
    .muted { color: #6b5b4a; font-style: italic; }
    @media print { @page { size: A4 portrait; margin: 10mm; } .page { max-width: 100%; padding: 0; } .grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
  </style>
</head>
<body>
  <main class="page">
    <section class="hero">
      <h1>${escapeHtml(title)} | Personel Sunum Dosyası</h1>
      <p class="meta">
        <span class="chip">Kampanya: ${escapeHtml(campaignName)}</span>
        <span class="chip">Dönem: ${escapeHtml(dateRange)}</span>
        <span class="chip">Çıktı: ${escapeHtml(new Date().toLocaleDateString('tr-TR'))}</span>
      </p>
    </section>
    <section class="grid">
      <article class="card"><h2>Kampanya Özeti</h2><p>${escapeHtml(goal)}</p></article>
      <article class="card"><h2>Kampanya Hedefi</h2><p>${escapeHtml(targetText)}</p></article>
      <article class="card">${storeSection}</article>
      <article class="card">${staffSection}</article>
      ${customSection ? `<article class="card">${customSection}</article>` : ''}
    </section>
    <section class="card full">
      <h2>Paylaşım Notu</h2>
      <p>${escapeHtml(getSafeString(output.staffShareNote, '-'))}</p>
    </section>
  </main>
</body>
</html>`;

    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    iframe.style.visibility = 'hidden';
    document.body.appendChild(iframe);
    const frameWindow = iframe.contentWindow;
    if (!frameWindow) {
      iframe.remove();
      return;
    }
    frameWindow.document.open();
    frameWindow.document.write(html);
    frameWindow.document.close();
    window.setTimeout(() => {
      frameWindow.focus();
      frameWindow.print();
      window.setTimeout(() => iframe.remove(), 800);
    }, 150);
  };

  if (!hasActiveCampaign) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { bgcolor: '#fffaf0', color: '#2f2516', border: '1px solid rgba(151,113,39,0.24)', borderRadius: 2 } }}>
        <DialogTitle sx={{ color: '#2f2516', fontWeight: 900 }}>Aktif kampanya bulunamadı</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#5f5140' }}>Rapor oluşturmak için önce bir kampanya oluşturun veya Kampanyalar listesinden görüntüleyin.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} sx={{ textTransform: 'none', color: '#7a4d0b' }}>Kapat</Button>
        </DialogActions>
      </Dialog>
    );
  }

  if (!output) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{
        className: 'staff-output-paper',
        sx: {
          background: 'linear-gradient(180deg, #fffaf0 0%, #f3eadb 100%)',
          border: '1px solid rgba(151,113,39,0.24)',
          borderRadius: 2,
          color: '#2f2516',
          width: output?.campaignFile ? 'min(94vw, 1180px)' : undefined,
          height: output?.campaignFile ? '88vh' : undefined,
          maxHeight: '92vh',
          overflow: 'hidden'
        }
      }}
    >
      <style>
        {`
          @media print {
            @page {
              size: A4 portrait;
              margin: 10mm;
            }

            * {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }

            html,
            body {
              background: #f8f4ea !important;
            }

            body * {
              visibility: hidden !important;
            }

            .staff-output-print-area,
            .staff-output-print-area * {
              visibility: visible !important;
            }

            .staff-output-paper {
              position: static !important;
              max-height: none !important;
              overflow: visible !important;
              box-shadow: none !important;
              border: 0 !important;
              background: #f8f4ea !important;
            }

            .staff-output-print-area {
              position: absolute !important;
              left: 0 !important;
              right: 0 !important;
              top: 0 !important;
              width: auto !important;
              max-width: 100% !important;
              box-sizing: border-box !important;
              max-height: none !important;
              overflow: visible !important;
              background: #f8f4ea !important;
              color: #2f2516 !important;
              padding: 6mm !important;
              display: block !important;
              border: 1px solid #d7a447 !important;
              border-radius: 10px !important;
            }

            .staff-output-print-title {
              display: block !important;
            }

            .staff-output-screen-title,
            .staff-output-actions {
              display: none !important;
            }

            .staff-output-section,
            .staff-output-plan-item {
              break-inside: avoid !important;
              page-break-inside: avoid !important;
            }

            .staff-output-section {
              box-sizing: border-box !important;
              background: #ffffff !important;
              border: 1px solid #d9c4a0 !important;
              box-shadow: none !important;
              margin-bottom: 8px !important;
              padding: 9px !important;
            }

            .staff-output-plan-item {
              box-sizing: border-box !important;
              background: #ffffff !important;
              border: 1px solid #e8d7b8 !important;
              margin-bottom: 7px !important;
            }

            .staff-output-two-column {
              display: grid !important;
              grid-template-columns: 1fr 1fr !important;
              gap: 8px !important;
              box-sizing: border-box !important;
              width: 100% !important;
            }

            .staff-output-plan-number {
              background: #9a6a1f !important;
              color: #fff8e1 !important;
              border: 1px solid #7a4d0b !important;
            }

            .staff-output-print-title {
              background: linear-gradient(135deg, #4a3415 0%, #9a6a1f 100%) !important;
              border-radius: 10px !important;
              padding: 10px 12px !important;
              margin-bottom: 12px !important;
            }

            .staff-output-print-title * {
              color: #fff8e1 !important;
            }

            .staff-output-section h1,
            .staff-output-section h2,
            .staff-output-section h3,
            .staff-output-section .MuiTypography-root:first-child {
              color: #7a4d0b !important;
            }

            .staff-output-section * {
              color: #2f2516 !important;
            }
          }
        `}
      </style>
      <DialogTitle sx={{ px: 1.6, pt: 1.1, pb: 0.7, borderBottom: '1px solid rgba(151,113,39,0.14)' }}>
        <Typography sx={{ color: '#2f2516', fontWeight: 900, fontSize: '1.12rem', letterSpacing: '0.01em' }}>
          {getSafeString(output.title, 'Personel Çıktısı')}
        </Typography>
        <Typography sx={{ color: '#6b5b40', fontSize: '0.78rem', mt: 0.15 }}>
          Personele verilebilir sade görev sunumu
        </Typography>
      </DialogTitle>
      <DialogContent
        className="staff-output-print-area"
        sx={{
          display: 'grid',
          gap: output.campaignFile ? 1.1 : 0.55,
          maxHeight: output.campaignFile ? 'calc(88vh - 92px)' : 'calc(92vh - 92px)',
          overflow: output.campaignFile ? 'auto' : 'hidden',
          px: output.campaignFile ? 2 : 1.6,
          py: output.campaignFile ? 1.1 : 0.55,
          '&::-webkit-scrollbar': { width: 8 },
          '&::-webkit-scrollbar-track': { background: 'rgba(151,113,39,0.08)' },
          '&::-webkit-scrollbar-thumb': { background: 'rgba(151,113,39,0.28)', borderRadius: 99 }
        }}
      >
        <Box className="staff-output-print-title" sx={{ display: 'none', mb: 1.4 }}>
          <Typography sx={{ color: '#fff8e1', fontSize: '1.28rem', fontWeight: 900, letterSpacing: '0.02em' }}>UKBA GOLD | Personel Görev Planı</Typography>
          <Typography sx={{ color: '#fff8e1', fontSize: '0.9rem', fontWeight: 800, mt: 0.35 }}>
            {getSafeString(output.title, 'Personel Çıktısı')}
          </Typography>
          {printDateRange && (
            <Typography sx={{ color: '#fff3c4', fontSize: '0.8rem', mt: 0.2 }}>
              Tarih Aralığı: {printDateRange}
            </Typography>
          )}
          <Typography sx={{ color: '#fff3c4', fontSize: '0.8rem', mt: 0.25 }}>
            {getSafeString(output.summary, '-')}
          </Typography>
        </Box>

        <Box className="staff-output-section" sx={{ ...sectionSx, p: 1.35, background: 'linear-gradient(135deg, #fffdf8 0%, #f6ead1 100%)', borderLeft: '5px solid #9a6a1f' }}>
          <Typography sx={{ color: '#2f2516', fontSize: '1.22rem', fontWeight: 900 }}>
            {getSafeString(output.subtitle, 'Personele Paylaşılacak Kampanya Uygulama Dosyası')}
          </Typography>
          <Typography sx={{ ...textSx, mt: 0.6 }}>{getSafeString(output.summary, '-')}</Typography>
          <Typography sx={{ ...textSx, mt: 0.55, color: '#7a4d0b', fontWeight: 900 }}>{getSafeString(output.staffShareNote, '-')}</Typography>
        </Box>

        <Box className="staff-output-section" sx={sectionSx}>
          <Typography sx={titleSx}>Kampanya Detayları</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 0.8 }}>
            {[
              ['Kampanya', output.campaignDetails?.name],
              ['Tarih', output.campaignDetails?.dateRange],
              ['Hedef', output.campaignDetails?.goal],
              ['Kampanya Hedefi', output.campaignDetails?.targetText || output.campaignDetails?.goal]
            ].map(([label, value]) => (
              <Box key={label} sx={{ borderRadius: 1.35, border: '1px solid rgba(151,113,39,0.14)', p: 0.85, background: 'rgba(255,255,255,0.68)' }}>
                <Typography sx={labelSx}>{label}</Typography>
                <Typography sx={valueSx}>{getSafeString(value, '-')}</Typography>
              </Box>
            ))}
          </Box>
        </Box>

        <Box className="staff-output-two-column" sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.1 }}>
          <PlanSection title="Mağaza" items={output.approvedStorePlan} emptyText="Seçilmiş mağaza fikri yok." />
          <PlanSection title="Personel" items={output.approvedStaffPlan} emptyText="Seçilmiş personel fikri yok." />
        </Box>
        <PlanSection title="Yeni Fikir" items={output.approvedCustomIdeas} emptyText="Ekstra yeni fikir eklenmemiş." />
      </DialogContent>
      <DialogActions
        className="staff-output-actions"
        sx={{
          px: 1.6,
          py: 0.75,
          borderTop: '1px solid rgba(151,113,39,0.14)',
          background: 'rgba(255,250,240,0.96)',
          position: 'sticky',
          bottom: 0
        }}
      >
        <Button onClick={onClose} sx={{ textTransform: 'none', color: '#4b5563' }}>Kapat</Button>
        <Button onClick={handlePrint} sx={{ textTransform: 'none', color: '#7a4d0b' }}>Yazdır</Button>
        <Button
          variant="contained"
          onClick={handleCopy}
          sx={{ textTransform: 'none', color: '#fff8e1', fontWeight: 800, bgcolor: '#9a6a1f', '&:hover': { bgcolor: '#7a4d0b' } }}
        >
          Kopyala
        </Button>
      </DialogActions>
      <style>{`
        @media (max-width: 600px) {
          .staff-output-paper {
            width: 100vw !important;
            max-width: 100vw !important;
            height: 100svh !important;
            max-height: 100svh !important;
            margin: 0 !important;
            border-radius: 0 !important;
          }

          .staff-output-actions {
            flex-wrap: wrap !important;
            gap: 6px !important;
          }

          .staff-output-actions .MuiButton-root {
            flex: 1 1 calc(50% - 4px) !important;
            min-height: 40px !important;
          }
        }
      `}</style>
    </Dialog>
  );
}

export default StaffOutputModal;
